import { z } from 'zod'

// Pure inventory-availability helpers + types, kept free of the api/client chain
// (which touches `window` at load) so they can be unit-tested under bun:test.
// The data-fetching hooks live in src/lib/api/inventory.ts, which re-exports this.

// One row per product x location x bin in the NetSuite->Dataverse mirror table
// (dsy_ns_inventorybalances). `dsy_quantityavailable` is pre-computed on the
// NetSuite side, already net of picked + committed stock.
export const inventoryBalanceSchema = z.object({
  _dsy_product_value: z.string().nullable(),
  dsy_quantityavailable: z.number().nullable(),
  dsy_locationname: z.string().nullable(),
  dsy_binname: z.string().nullable(),
  // NetSuite internal ids, needed to designate a bin for item fulfillment.
  dsy_location: z.number().nullable(),
  dsy_binnumber: z.number().nullable(),
})
export type InventoryBalanceRow = z.infer<typeof inventoryBalanceSchema>

export type InventoryAvailabilityDetail = {
  locationName: string | null
  binName: string | null
  locationId: number | null
  binNumber: number | null
  qtyAvailable: number
}

export type ProductInventoryAvailability = {
  qtyAvailable: number
  details: InventoryAvailabilityDetail[]
}

export type InventoryAvailabilityByProduct = Map<string, ProductInventoryAvailability>

export type InventoryLocationGroup = {
  locationName: string | null
  bins: InventoryAvailabilityDetail[]
}

function encodeODataLiteral(value: string): string {
  return encodeURIComponent(value.replace(/'/g, "''"))
}

export function buildInventoryAvailabilityFilter(productIds: readonly string[], branchId: string): string {
  const productFilter = productIds
    .map((id) => `_dsy_product_value eq '${encodeODataLiteral(id)}'`)
    .join(' or ')
  return `(${productFilter}) and _dsy_branch_value eq '${encodeODataLiteral(branchId)}' and dsy_quantityavailable gt 0 and statecode eq 0`
}

// Sum availability per product across all of its location/bin rows, retaining the
// per-row detail (sorted by location then bin) for a breakdown tooltip. Rows with
// no product or non-positive quantity are skipped.
export function aggregateInventoryAvailability(rows: InventoryBalanceRow[]): InventoryAvailabilityByProduct {
  const byProduct: InventoryAvailabilityByProduct = new Map()

  for (const row of rows) {
    const productId = row._dsy_product_value
    const qtyAvailable = row.dsy_quantityavailable ?? 0
    if (!productId || qtyAvailable <= 0) continue

    const current = byProduct.get(productId) ?? { qtyAvailable: 0, details: [] }
    current.qtyAvailable += qtyAvailable
    current.details.push({
      locationName: row.dsy_locationname,
      binName: row.dsy_binname,
      locationId: row.dsy_location,
      binNumber: row.dsy_binnumber,
      qtyAvailable,
    })
    byProduct.set(productId, current)
  }

  for (const availability of byProduct.values()) {
    availability.details.sort((a, b) => {
      const byLocation = (a.locationName ?? '').localeCompare(b.locationName ?? '', undefined, { numeric: true })
      if (byLocation !== 0) return byLocation
      return (a.binName ?? '').localeCompare(b.binName ?? '', undefined, { numeric: true })
    })
  }

  return byProduct
}

export type UsedInventoryDesignationPatch = {
  dsy_usedinventoryquantity: number | null
  dsy_usedinventorylocation?: null
  dsy_usedinventorylocationname?: null
  dsy_usedinventorybin?: null
  dsy_usedinventorybinname?: null
}

// Keep a shrinking line's truck-stock designation consistent: clamp the
// designated qty to the line's new whole-unit quantity, clear entirely when
// fewer than one whole unit remains (a sub-1 line either never publishes to
// the NetSuite BOM or can't cover one designated unit — both would orphan the
// designation). Fulfilled designations (NetSuite id written back) are
// immutable history. Returns null when no adjustment is needed.
export function clampUsedInventoryDesignation(
  cpq: { dsy_usedinventoryquantity?: number | null; dsy_usedinventoryfulfillmentid?: string | null },
  newQuantity: number
): UsedInventoryDesignationPatch | null {
  const designated = cpq.dsy_usedinventoryquantity
  if (designated == null || cpq.dsy_usedinventoryfulfillmentid) return null
  const wholeUnits = Math.floor(newQuantity)
  if (wholeUnits < 1) {
    return {
      dsy_usedinventoryquantity: null,
      dsy_usedinventorylocation: null,
      dsy_usedinventorylocationname: null,
      dsy_usedinventorybin: null,
      dsy_usedinventorybinname: null,
    }
  }
  if (designated > wholeUnits) return { dsy_usedinventoryquantity: wholeUnits }
  return null
}

// Group a product's location/bin detail rows by location for display. `details`
// is expected pre-sorted (aggregateInventoryAvailability sorts them), so the
// insertion-ordered Map preserves location then bin order.
export function groupAvailabilityByLocation(details: InventoryAvailabilityDetail[]): InventoryLocationGroup[] {
  const groups = new Map<string, InventoryLocationGroup>()
  for (const detail of details) {
    const key = detail.locationName ?? ''
    const group = groups.get(key) ?? { locationName: detail.locationName, bins: [] }
    group.bins.push(detail)
    groups.set(key, group)
  }
  return [...groups.values()]
}
